import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import {
  ShieldCheck, RefreshCw, Timer, Sparkles, LifeBuoy, MessageCircle,
  Wallet, AlertTriangle, CheckCircle2, Lock,
} from "lucide-react";

const Step = ({ n, title, body, icon: Icon }) => (
  <div className="relative bg-white rounded-2xl border border-black/10 p-6">
    <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-[#1A2024] text-[#D9923B] font-display text-lg flex items-center justify-center shadow-sm">{n}</div>
    <Icon className="w-5 h-5 text-[#C05A3C] mb-3" />
    <div className="font-semibold mb-1">{title}</div>
    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
  </div>
);

const BuyerGuarantee = () => {
  const [trust, setTrust] = useState(null);

  useEffect(() => {
    api.get("/stats/trust").then((r) => setTrust(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <SEO
        title="Buyer Guarantee — your money is safe"
        description="Every AfriMarket purchase is protected. Hold-and-release escrow, 7-day refund window, real-human dispute resolution, ID-verified sellers. Shop with confidence."
        path="/buyer-guarantee"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "Is my money safe on AfriMarket?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Payments are held in escrow with Stripe and PayPal until you confirm receipt. If the item doesn't arrive or doesn't match the listing, you're refunded — every time." } },
            { "@type": "Question", "name": "How long do I have to request a refund?", "acceptedAnswer": { "@type": "Answer", "text": "You have 7 days from delivery to raise a complaint and request a refund through our resolution centre." } },
            { "@type": "Question", "name": "Are sellers verified?", "acceptedAnswer": { "@type": "Answer", "text": "All sellers on AfriMarket complete an ID and proof-of-address check before they can sell. Verified sellers carry a blue badge so you can shop with confidence." } },
            { "@type": "Question", "name": "What if I have a dispute with a seller?", "acceptedAnswer": { "@type": "Answer", "text": "Open a case in our Resolution Centre. A real human reviewer investigates within 48 hours and resolves it fairly — refund, partial refund, or release to the seller." } },
            { "@type": "Question", "name": "Does AfriMarket charge buyers?", "acceptedAnswer": { "@type": "Answer", "text": "No. Buyers pay nothing extra — only the listed price plus shipping. AfriMarket earns a small commission from sellers, not buyers." } },
          ],
        }}
      />
      {/* Hero */}
      <section className="bg-[#FBF6EE] border-b border-black/5">
        <div className="container-narrow py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="label-overline text-[#C05A3C] mb-3">Buyer guarantee</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Shop with confidence. <br/>
              <span className="text-[#C05A3C]">A real human</span> has your back.
            </h1>
            <p className="mt-6 text-muted-foreground font-sans-ui max-w-2xl leading-relaxed text-base">
              AfriMarket isn&apos;t a faceless marketplace. Every transaction is backed by our
              Resolution Centre — open a case and we hold the seller&apos;s payout while we
              investigate. No bots, no scripted refusals. Just diaspora-to-diaspora trust.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/complaints" data-testid="bg-cta-open-case">
                <Button className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md h-12 px-6">
                  <LifeBuoy className="w-4 h-4 mr-2" /> Open a case
                </Button>
              </Link>
              <Link to="/browse" className="inline-flex items-center text-sm font-medium underline hover:text-[#C05A3C]" data-testid="bg-cta-browse">
                Start shopping →
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-[#1A2024] text-white rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute inset-0 ankara-stripes opacity-10 pointer-events-none" />
              <ShieldCheck className="w-9 h-9 text-[#D9923B] mb-4" />
              <div className="font-display text-3xl leading-tight">The promise</div>
              <ul className="mt-5 space-y-3 text-sm leading-relaxed">
                <li className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-[#D9923B] shrink-0 mt-0.5" /> Every seller is KYC-verified before they can publish.</li>
                <li className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-[#D9923B] shrink-0 mt-0.5" /> Payout is held automatically when you open a payment dispute.</li>
                <li className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-[#D9923B] shrink-0 mt-0.5" /> Decisions are made by a real human, not an algorithm.</li>
                <li className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-[#D9923B] shrink-0 mt-0.5" /> Bad-actor listings and accounts are removed on resolution.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stats from /stats/trust */}
      {trust && trust.total_cases > 0 && (
        <section className="container-narrow py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-black/10 p-6" data-testid="bg-tile-resolution">
              <ShieldCheck className="w-5 h-5 text-[#4A5D23] mb-3" />
              <div className="font-display text-4xl leading-none">{trust.resolution_rate_pct}%</div>
              <div className="text-xs text-muted-foreground mt-2">cases resolved or closed</div>
            </div>
            <div className="bg-white rounded-2xl border border-black/10 p-6" data-testid="bg-tile-refunds">
              <RefreshCw className="w-5 h-5 text-[#C05A3C] mb-3" />
              <div className="font-display text-4xl leading-none">{trust.refunds_issued}</div>
              <div className="text-xs text-muted-foreground mt-2">refunds issued to buyers</div>
            </div>
            <div className="bg-[#1A2024] text-white rounded-2xl p-6" data-testid="bg-tile-time">
              <Timer className="w-5 h-5 text-[#D9923B] mb-3" />
              <div className="font-display text-4xl leading-none">{trust.avg_resolution_hours != null ? `${trust.avg_resolution_hours}h` : "—"}</div>
              <div className="text-xs text-white/70 mt-2">average case-to-close time</div>
            </div>
            <div className="bg-white rounded-2xl border border-black/10 p-6" data-testid="bg-tile-removed">
              <Sparkles className="w-5 h-5 text-[#C05A3C] mb-3" />
              <div className="font-display text-4xl leading-none">{trust.listings_removed + trust.users_suspended}</div>
              <div className="text-xs text-muted-foreground mt-2">bad-actor listings &amp; accounts removed</div>
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="bg-white border-y border-black/5">
        <div className="container-narrow py-16">
          <div className="label-overline text-[#C05A3C] mb-2">If something goes wrong</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-2">Three steps. That&apos;s it.</h2>
          <p className="text-muted-foreground max-w-2xl mb-10 font-sans-ui">
            Most issues get sorted between buyer and seller in the first message. When they don&apos;t,
            here&apos;s exactly what happens.
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            <Step
              n="1"
              icon={MessageCircle}
              title="Talk to the seller first"
              body="Send a message from the listing page. 80% of cases resolve right here — wrong colour, late shipping, simple miscommunication. Keep the receipts."
            />
            <Step
              n="2"
              icon={LifeBuoy}
              title="Open a case"
              body="From your dashboard, hit 'Open a case' and pick the category. Upload screenshots, photos, anything that helps. The seller's payout is paused immediately for payment disputes."
            />
            <Step
              n="3"
              icon={Wallet}
              title="We decide — fast"
              body="Both sides reply in-thread. Our team reviews evidence, talks to both parties if needed, and issues a refund, warning, suspension or dismisses the case. Most cases close within 48 hours."
            />
          </div>
        </div>
      </section>

      {/* What's covered / What's not */}
      <section className="container-narrow py-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-[#4A5D23]/5 border border-[#4A5D23]/20 rounded-2xl p-7">
            <CheckCircle2 className="w-6 h-6 text-[#4A5D23] mb-3" />
            <div className="font-display text-2xl mb-3">Covered</div>
            <ul className="space-y-2 text-sm text-foreground/85 leading-relaxed">
              <li>· Items that never arrive</li>
              <li>· Items dramatically different from the listing description</li>
              <li>· Fake or counterfeit goods</li>
              <li>· Verified scam attempts</li>
              <li>· Harassment or abusive behaviour from sellers</li>
              <li>· Prohibited or illegal listings</li>
            </ul>
          </div>
          <div className="bg-[#5C5852]/5 border border-black/10 rounded-2xl p-7">
            <AlertTriangle className="w-6 h-6 text-[#B84A28] mb-3" />
            <div className="font-display text-2xl mb-3">Not covered</div>
            <ul className="space-y-2 text-sm text-foreground/85 leading-relaxed">
              <li>· Buyer&apos;s remorse (you changed your mind)</li>
              <li>· Damage caused after delivery / use</li>
              <li>· Off-platform transactions (always pay through AfriMarket)</li>
              <li>· Customs duties or import taxes</li>
              <li>· Disputes opened more than 30 days after delivery</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Pay in-app reminder */}
      <section className="bg-[#1A2024] text-white">
        <div className="container-narrow py-16 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="label-overline text-[#D9923B] mb-3">Pay through AfriMarket</div>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight">
              If you pay off-platform, we can&apos;t protect you.
            </h2>
            <p className="mt-4 text-white/75 max-w-2xl">
              Bank transfers, gift cards, crypto, &ldquo;just send it on PayPal Friends&rdquo; —
              none of those leave us a record to hold. Stick with the Stripe or PayPal Pay button
              on every listing and you&apos;re fully covered.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 backdrop-blur-sm">
              <Lock className="w-6 h-6 text-[#D9923B] mb-3" />
              <div className="text-sm leading-relaxed">
                <b>How payouts work:</b> sellers get paid after the order is confirmed delivered.
                If a complaint is opened in the meantime, the funds stay frozen until the case
                closes.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container-narrow py-20 text-center">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Found a problem?</h2>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Open a case in 60 seconds. We&apos;ll take it from there.</p>
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/complaints/new" data-testid="bg-final-cta">
            <Button className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md h-12 px-7">
              <LifeBuoy className="w-4 h-4 mr-2" /> File a complaint
            </Button>
          </Link>
          <Link to="/complaints" className="inline-flex items-center text-sm font-medium underline hover:text-[#C05A3C]">
            View my cases
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BuyerGuarantee;
